import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  CheckCircle2,
  FastForward,
  Headphones,
  HelpCircle,
  Loader2,
  PlayCircle,
} from 'lucide-react';

import {
  courseQueries,
  type Content,
  type CourseContent,
} from '@/entities/course';
import { getUsersMe, patchUsersMe, usersQueries } from '@/entities/user';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/shared/ui/button';
import { useToast } from '@/shared/ui/toast-provider';

const heroImageUrl =
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=3540&auto=format&fit=crop';
const formatCourseDuration = (durationSecs: number) => {
  if (!durationSecs) return 'Course';
  const hours = Math.floor(durationSecs / 3600);
  const minutes = Math.floor((durationSecs % 3600) / 60);
  if (hours < 1) {
    return `${minutes} Minute${minutes === 1 ? '' : 's'}`;
  }
  return `${hours} Hour${hours === 1 ? '' : 's'}`;
};

const formatModuleCreditHours = (creditHours: number) => {
  if (!creditHours) return '0h';
  if (Number.isInteger(creditHours)) {
    return `${creditHours}h`;
  }
  return `${creditHours.toFixed(1)}h`;
};

const findActiveCourse = (courses: CourseContent[]) => {
  if (!courses.length) return null;
  const withAssociation = courses.find(
    (course) => course.user_course_association,
  );
  if (withAssociation) return withAssociation;
  const active = courses.find((course) => course.is_active);
  return active ?? courses[0];
};

const findNextIncompleteContent = (course: CourseContent | null) => {
  if (!course) return undefined;
  return (
    course.contents.find(
      (item) => !item.user_content_association?.is_completed,
    ) ?? course.contents.find((item) => item.content_type === 'quiz')
  );
};

const ModuleStatus = ({ completed }: { completed: boolean }) => (
  <span
    className={cn(
      'flex size-7 shrink-0 items-center justify-center rounded-full border-2',
      completed
        ? 'border-emerald-500 bg-emerald-500 text-white'
        : 'border-slate-200 bg-white text-slate-400',
    )}
  >
    {completed ? (
      <CheckCircle2 className="h-5 w-5" />
    ) : (
      <span className="block h-2.5 w-2.5 rounded-full bg-slate-300" />
    )}
  </span>
);

const ModuleItem = ({
  content,
  onSelect,
}: {
  content: Content;
  onSelect: (content: Content) => void;
}) => {
  const completed = Boolean(content.user_content_association?.is_completed);
  const hasQuestions = content.quizzes.length > 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(content)}
      className="flex w-full items-center justify-between rounded-3xl bg-slate-100/80 px-5 py-4 text-left shadow-[0_4px_18px_rgba(15,23,42,0.06)] transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      data-testid="course-card"
    >
      <div className="flex flex-1 items-center gap-3">
        <ModuleStatus completed={completed} />
        <span
          className={cn(
            'text-base font-medium text-slate-900 transition-colors',
            completed && 'text-slate-400 line-through',
          )}
        >
          {content.title}
        </span>
      </div>
      <div className="ml-4 flex flex-col items-end gap-1 text-xs font-medium text-slate-500">
        {hasQuestions ? (
          <span className="flex items-center gap-1 whitespace-nowrap">
            <HelpCircle className="h-4 w-4" />
            {content.quizzes.length} Questions
          </span>
        ) : (
          <span>{formatModuleCreditHours(content.credit_hours)}</span>
        )}
      </div>
    </button>
  );
};

export const CoursesPage = () => {
  const router = useRouter();
  const toast = useToast();
  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error } = useQuery(courseQueries.list());
  const courses = data ?? [];
  const trackedCompletedModulesRef = useRef(new Set<string>());
  const trackedCompletedCoursesRef = useRef(new Set<string>());
  const activeCourse = findActiveCourse(courses);
  const totalQuestions =
    activeCourse?.contents.reduce(
      (acc, content) => acc + content.quizzes.length,
      0,
    ) ?? 0;

  const { mutateAsync: fetchUser, isPending: isFetchingUser } = useMutation({
    mutationFn: getUsersMe,
  });

  const { mutateAsync: disableServerPlayback, isPending: isDisablingPlayback } =
    useMutation({
      mutationFn: (payload: { is_server_playback_mode_active: boolean }) =>
        patchUsersMe(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: usersQueries.me().queryKey,
        });
      },
    });

  useEffect(() => {
    if (isError) {
      toast({
        type: 'error',
        title: 'We could not load the courses.',
        description: resolveErrorMessage(error, 'Please try again later.'),
      });
    }
  }, [isError, error, toast]);

  useEffect(() => {
    if (!courses.length) return;

    courses.forEach((course) => {
      const contents = course.contents ?? [];
      const totalModules = contents.length;
      let completedModules = 0;

      contents.forEach((content, index) => {
        const isCompleted = Boolean(
          content.user_content_association?.is_completed,
        );
        if (isCompleted) {
          completedModules += 1;
          if (!trackedCompletedModulesRef.current.has(content.id)) {
            captureAnalyticsEvent('course_module_completed', {
              course_id: course.id,
              course_title: course.title,
              content_id: content.id,
              content_title: content.title,
              content_order: index + 1,
              content_type: content.content_type,
              credit_hours: content.credit_hours,
              duration_secs: content.duration_secs,
            });
            trackedCompletedModulesRef.current.add(content.id);
          }
        }
      });

      const courseProgress =
        course.user_course_association?.progress_percent ?? null;

      if (
        totalModules > 0 &&
        completedModules === totalModules &&
        !trackedCompletedCoursesRef.current.has(course.id)
      ) {
        captureAnalyticsEvent('course_completed', {
          course_id: course.id,
          course_title: course.title,
          total_modules: totalModules,
          completed_modules: completedModules,
          duration_secs: course.duration_secs,
          progress_percent: courseProgress,
          certificate_available: Boolean(course.user_certificates),
        });
        trackedCompletedCoursesRef.current.add(course.id);
      }
    });
  }, [courses]);

  const handleNavigateToContent = useCallback(
    (content: Content) => {
      if (content.content_type === 'quiz') {
        const quizId = content.quizzes.find(
          (quiz) =>
            !quiz?.user_content_quiz_association ||
            quiz?.user_content_quiz_association?.answer_bool === null,
        )?.id;
        router.navigate({
          to: '/courses/quiz',
          search: {
            courseId: activeCourse!.id,
            contentId: content.id,
            quizId: quizId ?? '',
          },
        });
        return;
      }
      router.navigate({
        to: '/courses/plyer',
        search: {
          courseId: activeCourse!.id,
          contentId: content.id,
        },
      });
    },
    [router, activeCourse],
  );

  const handleModuleSelect = useCallback(
    (content: Content) => {
      if (!activeCourse) return;
      const contents = activeCourse.contents;
      const index = contents.findIndex((item) => item.id === content.id);
      if (index > 0) {
        const previous = contents[index - 1];
        if (!previous) {
          return;
        }
        if (!previous.user_content_association?.is_completed) {
          toast({
            type: 'info',
            title: 'Hold on',
            description:
              'Please complete the previous module before moving ahead.',
          });
          return;
        }
      }
      handleNavigateToContent(content);
    },
    [activeCourse, handleNavigateToContent, toast],
  );

  const handleStartCourse = useCallback(async () => {
    if (!activeCourse) return;
    const nextContent = findNextIncompleteContent(activeCourse);
    if (!nextContent) {
      toast({
        type: 'info',
        title: 'All modules completed',
        description:
          'You can revisit any module from the list to review the material.',
      });
      return;
    }

    try {
      if (nextContent.content_type !== 'quiz') {
        const user = await fetchUser();
        if (user?.is_server_playback_mode_active) {
          await disableServerPlayback({
            is_server_playback_mode_active: false,
          });
        }
      }

      handleNavigateToContent(nextContent);
    } catch (mutationError) {
      toast({
        type: 'error',
        title: 'Unable to open the module.',
        description: resolveErrorMessage(
          mutationError,
          'Please try again in a moment.',
        ),
      });
    }
  }, [
    activeCourse,
    disableServerPlayback,
    fetchUser,
    handleNavigateToContent,
    toast,
  ]);

  const isActionPending = isFetchingUser || isDisablingPlayback;

  if (isPending) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-900">
        <div className="flex items-center gap-3 rounded-2xl bg-white px-6 py-4 shadow-[0_20px_60px_rgba(15,23,42,0.12)]">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
          <span className="text-sm font-medium text-slate-600">
            Loading your courses…
          </span>
        </div>
      </main>
    );
  }

  if (!activeCourse) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 px-6 text-slate-900">
        <section className="w-full max-w-md rounded-[32px] bg-white/95 p-8 text-center shadow-[0_40px_80px_rgba(15,23,42,0.12)]">
          <h2 className="text-2xl font-semibold text-slate-900">
            No courses available
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            We could not find any active courses for your account. Reach out to
            your administrator if you believe this is a mistake.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="relative flex min-h-screen justify-center bg-slate-100 pb-28 text-slate-900">
      <div className="flex w-full max-w-[420px] flex-col pb-10">
        <div className="relative h-[260px] w-full overflow-hidden">
          <img
            src={heroImageUrl}
            alt="Downtown Seattle skyscrapers"
            className="h-full w-full object-cover"
          />
        </div>
        <section className="-mt-6 flex flex-1 flex-col rounded-t-[32px] bg-white px-6 pb-10 pt-8 shadow-[0_-18px_40px_rgba(15,23,42,0.12)]">
          <header className="space-y-4">
            <h1 className="text-2xl font-semibold text-slate-900">
              {formatCourseDuration(activeCourse.duration_secs)}{' '}
              {activeCourse.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-slate-600">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
                <BookOpen className="h-4 w-4 text-emerald-500" />
                WA License Renewal
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-sky-700">
                <Headphones className="h-4 w-4 text-sky-500" />
                Audio Format
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-violet-700">
                <FastForward className="h-4 w-4 text-violet-500" />
                Binge Learning
              </span>
            </div>
            <p className="text-sm leading-relaxed text-slate-500">
              {activeCourse.description}
            </p>
          </header>

          <section className="mt-8 space-y-4">
            <div className="flex items-center justify-between text-sm font-semibold text-slate-600">
              <span>Courses</span>
              <span className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <HelpCircle className="h-4 w-4" />
                {totalQuestions} Questions
              </span>
            </div>
            <div className="space-y-3" data-testid="course-list">
              {activeCourse.contents.map((content) => (
                <ModuleItem
                  key={content.id}
                  content={content}
                  onSelect={handleModuleSelect}
                />
              ))}
            </div>
          </section>

          <div className="mt-10">
            <Button
              onClick={handleStartCourse}
              disabled={isActionPending}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-[18px] bg-linear-to-r from-[#4763E4] to-[#5A74F5] text-base font-semibold text-white shadow-[0_18px_35px_rgba(71,99,228,0.35)] transition hover:from-[#4158e4] hover:to-[#546cf2]"
            >
              {isActionPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <PlayCircle className="h-5 w-5" />
              )}
              Start Course
            </Button>
          </div>
        </section>
      </div>
    </main>
  );
};
